import React, { useEffect, useState, Suspense, lazy, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { SecurityScoreGauge } from '@/components/dashboard/SecurityScoreGauge';
import { EnvironmentOverview } from '@/components/dashboard/EnvironmentOverview';
import { CriticalAlerts } from '@/components/dashboard/CriticalAlerts';
import { TopVulnerabilities } from '@/components/dashboard/TopVulnerabilities';
import { ComplianceStatus } from '@/components/dashboard/ComplianceStatus';
import { PortStatus } from '@/components/dashboard/PortStatus';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { dashboardAPI, alertsAPI, vulnerabilitiesAPI, complianceAPI } from '@/lib/api';
import useDashboardStore from '@/store/dashboardStore';
import useAuthStore from '@/store/authStore';
import { Server, AlertTriangle, Shield, Bug, Activity, Radio, Zap, Crosshair, ChevronRight } from 'lucide-react';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const GlobalAttackMap = lazy(() => import('@/components/dashboard/GlobalAttackMap'));

const THREAT_NEWS = [
  { sev: 'critical', text: 'CVE-2024-3400 — PAN-OS zero-day sendo ativamente explorado em redes corporativas' },
  { sev: 'high', text: 'Grupo Scattered Spider mirando instituições financeiras com engenharia social' },
  { sev: 'critical', text: 'LockBit 4.0 com nova variante evadindo soluções EDR em endpoints Windows' },
  { sev: 'high', text: 'CISA alerta sobre campanha DarkGate via Microsoft Teams em empresas B2B' },
  { sev: 'critical', text: 'Ivanti Connect Secure explorado por APT chinês UNC5221 — patch urgente' },
  { sev: 'high', text: 'StopRansomware Advisory: BlackSuit atacando setor de saúde e infraestrutura crítica' },
  { sev: 'medium', text: 'Cisco IOS XE CVE-2023-20198 — patch disponível, atualize imediatamente' },
  { sev: 'critical', text: 'Cl0p ransomware explorando GoAnywhere MFT CVE-2023-0669 em larga escala' },
  { sev: 'high', text: 'SolarWinds Access Rights Manager — múltiplas RCE críticas divulgadas' },
  { sev: 'critical', text: 'MOVEit Transfer: novos IOCs detectados — verifique seus logs de transferência' },
  { sev: 'high', text: 'Campanha de phishing mirada em times de segurança via LinkedIn comprometido' },
  { sev: 'medium', text: 'Aumento de 340% em ataques de força bruta a RDP detectados esta semana' },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const {
    stats, attacks, alerts, vulnerabilities, compliance,
    setStats, setAttacks, setAlerts, setVulnerabilities, setCompliance,
    isLoading, setLoading
  } = useDashboardStore();

  const [attackStats, setAttackStats] = useState({ blocked: 0, detected: 0 });
  const [liveAlerts, setLiveAlerts] = useState([]);   // New alerts from WS
  const [wsStatus, setWsStatus] = useState('connecting');
  const [alertCount, setAlertCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [newsIdx, setNewsIdx] = useState(0);
  const [pulseAlert, setPulseAlert] = useState(false);
  const wsRef = useRef(null);

  // News ticker cycling
  useEffect(() => {
    const t = setInterval(() => setNewsIdx(i => (i + 1) % THREAT_NEWS.length), 5000);
    return () => clearInterval(t);
  }, []);

  // Initial data load
  useEffect(() => { loadDashboardData(); }, []);

  // Auto refresh every 20s
  useEffect(() => {
    const t = setInterval(loadDashboardData, 20000);
    return () => clearInterval(t);
  }, []);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, attacksRes, alertsRes, vulnsRes, compRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getAttacks(),
        alertsAPI.getAll({ limit: 50 }),
        vulnerabilitiesAPI.getAll(),
        complianceAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setAttacks(attacksRes.data.attacks || []);
      setAttackStats({ blocked: attacksRes.data.blocked || 0, detected: attacksRes.data.detected || 0 });
      setAlerts(alertsRes.data.alerts);
      setVulnerabilities(vulnsRes.data.vulnerabilities);
      setCompliance(compRes.data.compliance);
      setAlertCount(statsRes.data.alerts?.total || 0);
      setCriticalCount(statsRes.data.alerts?.critical || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  // WebSocket for real-time alerts
  useEffect(() => {
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/alerts?token=${token}`;

    let reconnectTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsStatus('live');
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            
            if (msg.type === 'new_alert') {
              const alert = msg.data;
              // Add to live alerts list (top)
              setLiveAlerts(prev => [alert, ...prev].slice(0, 8));
              // Merge into global alerts store
              setAlerts(prev => [alert, ...(Array.isArray(prev) ? prev : [])].slice(0, 50));
              // Increment counter
              setAlertCount(c => c + 1);
              if (alert.severity === 'critical') {
                setCriticalCount(c => c + 1);
                setPulseAlert(true);
                setTimeout(() => setPulseAlert(false), 2000);
                toast.error(`CRÍTICO: ${alert.title}`, { duration: 5000 });
              } else if (alert.severity === 'high') {
                toast.warning(`ALTO: ${alert.title}`, { duration: 4000 });
              }
            }
            
            if (msg.type === 'stats_update' || msg.type === 'heartbeat') {
              if (msg.open_alerts !== undefined) setAlertCount(msg.open_alerts);
              if (msg.critical !== undefined) setCriticalCount(msg.critical);
            }
            
            if (msg.type === 'init') {
              if (msg.open_alerts !== undefined) setAlertCount(msg.open_alerts);
              if (msg.critical !== undefined) setCriticalCount(msg.critical);
            }
          } catch (_) {}
        };

        ws.onerror = () => setWsStatus('offline');
        ws.onclose = () => {
          setWsStatus('offline');
          reconnectTimer = setTimeout(connect, 8000);
        };
      } catch (_) { setWsStatus('offline'); }
    };

    connect();
    return () => {
      clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [token]);

  // Also add new WS alerts to attacksData for globe
  const realtimeAttacks = useMemo(() => {
    const wsAtks = liveAlerts
      .filter(a => a.source_lat && a.source_lng && a.dest_lat && a.dest_lng)
      .map(a => ({ ...a }));
    return [...wsAtks, ...(Array.isArray(attacks) ? attacks : [])].slice(0, 50);
  }, [liveAlerts, attacks]);

  const envStats = {
    network: { total: stats?.assets?.total || 0, ok: stats?.assets?.online || 0, warning: 2, critical: 0 },
    endpoints: { total: 45, ok: 42, warning: 2, critical: 1 },
    servers: { total: 12, ok: 11, warning: 1, critical: 0 },
    cloud: { total: 8, ok: 8, warning: 0, critical: 0 },
    iot: { total: 15, ok: 12, warning: 2, critical: 1 },
  };

  const portStats = { total: 480, up: 456, down: 18, error: 6 };
  const news = THREAT_NEWS[newsIdx];

  return (
    <div className="min-h-screen flex flex-col" data-testid="dashboard-page">
      <Header title="Dashboard" />

      {/* Real-time news ticker */}
      <div className="border-b border-border bg-card/30 px-4 py-1.5 flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={cn(
            "h-2 w-2 rounded-full transition-colors",
            wsStatus === 'live' ? 'bg-emerald-400 animate-pulse shadow-[0_0_6px_#34d399]' :
            wsStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'
          )} />
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            {wsStatus === 'live' ? 'LIVE' : wsStatus === 'connecting' ? 'CONNECTING' : 'OFFLINE'}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground/50 shrink-0 font-mono border-l border-border pl-3">THREAT INTEL FEED</span>
        <div className="flex-1 overflow-hidden">
          <div key={newsIdx} className="flex items-center gap-2 text-xs font-mono animate-in slide-in-from-right-4 duration-500">
            <span className={cn(
              "font-bold shrink-0",
              news.sev === 'critical' ? 'text-red-400' : news.sev === 'high' ? 'text-orange-400' : 'text-blue-400'
            )}>
              [{news.sev.toUpperCase()}]
            </span>
            <span className="truncate text-muted-foreground">{news.text}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 border-l border-border pl-3">
          <Radio className="h-3 w-3 text-yellow-400/70 animate-pulse" />
          <span className="text-[10px] font-mono text-muted-foreground/50">AUTO 20s</span>
        </div>
      </div>

      <div className="flex-1 p-5 flex flex-col gap-4 min-h-0">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 shrink-0">
          <StatCard
            icon={Server} label="Total Assets" value={stats?.assets?.total || 0}
            subtext={`${stats?.assets?.online || 0} online`} color="text-blue-400"
            onClick={() => navigate('/assets')} testid="stat-total-assets"
          />
          <StatCard
            icon={AlertTriangle} label="Alertas Abertos" value={alertCount}
            subtext={`${criticalCount} críticos`}
            color={pulseAlert ? 'text-red-300' : 'text-red-400'}
            pulse={pulseAlert}
            onClick={() => navigate('/alerts')} testid="stat-open-alerts"
            badge={liveAlerts.length > 0 ? `+${liveAlerts.length} novos` : null}
          />
          <StatCard
            icon={Bug} label="Vulnerabilidades"
            value={(stats?.vulnerabilities?.critical || 0) + (stats?.vulnerabilities?.high || 0)}
            subtext={`${stats?.vulnerabilities?.critical || 0} críticas`} color="text-orange-400"
            onClick={() => navigate('/vulnerabilities')} testid="stat-vulns"
          />
          <StatCard
            icon={Shield} label="Security Score" value={stats?.security_score?.total_score || 0}
            subtext="de 100" color="text-emerald-400"
            onClick={() => navigate('/security-score')} testid="stat-score"
          />
        </div>

        {/* Main 3-column grid */}
        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
          {/* Globe — 8 cols */}
          <div className="col-span-12 lg:col-span-8 min-h-[420px]">
            <Card className="bg-card/30 border-border h-full overflow-hidden">
              <CardContent className="p-0 h-full">
                <Suspense fallback={
                  <div className="w-full h-full flex items-center justify-center bg-[#020817]">
                    <Activity className="h-8 w-8 animate-spin text-primary" />
                  </div>
                }>
                  <GlobalAttackMap
                    attacks={realtimeAttacks}
                    blockedToday={attackStats.blocked}
                    detectedToday={attackStats.detected}
                    newAlerts={liveAlerts}
                  />
                </Suspense>
              </CardContent>
            </Card>
          </div>

          {/* Right column — 4 cols */}
          <div className="col-span-12 lg:col-span-4 flex flex-col gap-4 min-h-0">
            {/* Security Score */}
            <Card className="bg-card/50 border-border shrink-0">
              <CardHeader className="pb-0 pt-4 px-4">
                <CardTitle className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Security Score
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <SecurityScoreGauge
                  score={stats?.security_score?.total_score || 0}
                  trend={3} lastIncident="há 2 dias"
                />
              </CardContent>
            </Card>

            {/* Live Alert Feed */}
            <Card className="bg-card/50 border-border flex-1 min-h-0 overflow-hidden">
              <CardHeader className="pb-0 pt-4 px-4 shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Zap className="h-3 w-3 text-yellow-400" />
                    Feed Ao Vivo
                  </CardTitle>
                  <div className="flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-ping absolute" />
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    <span className="text-[10px] font-mono text-red-400 ml-2">LIVE</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 overflow-y-auto h-full max-h-[280px]">
                <div className="space-y-2 mt-2">
                  {liveAlerts.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Radio className="h-6 w-6 mx-auto mb-2 animate-pulse" />
                      <p className="text-xs font-mono">Aguardando alertas...</p>
                    </div>
                  ) : (
                    liveAlerts.map((alert, i) => (
                      <div
                        key={alert.id || i}
                        className={cn(
                          "p-2 rounded border text-xs transition-all cursor-pointer hover:opacity-80",
                          i === 0 ? "animate-in slide-in-from-right-4 duration-500" : "",
                          alert.severity === 'critical' ? 'bg-red-950/40 border-red-500/30' :
                          alert.severity === 'high' ? 'bg-orange-950/40 border-orange-500/30' :
                          'bg-blue-950/40 border-blue-500/30'
                        )}
                        onClick={() => navigate('/alerts')}
                        data-testid={`live-alert-${alert.id}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className={cn(
                            "font-mono font-bold text-[9px] px-1.5 py-0.5 rounded",
                            alert.severity === 'critical' ? 'bg-red-500/20 text-red-300' :
                            alert.severity === 'high' ? 'bg-orange-500/20 text-orange-300' :
                            'bg-blue-500/20 text-blue-300'
                          )}>
                            {alert.severity?.toUpperCase()}
                          </span>
                          <span className="text-[9px] text-muted-foreground font-mono">
                            {alert.source_country || ''}
                          </span>
                        </div>
                        <p className="font-medium mt-0.5 leading-tight">{alert.title}</p>
                        {alert.mitre_technique && (
                          <span className="text-[9px] font-mono text-muted-foreground/70">{alert.mitre_technique}</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bottom row — all same fixed height */}
          <div className="col-span-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card/50 border-border h-[450px] overflow-hidden">
              <CardContent className="pt-4 pb-4 px-4 h-full overflow-y-auto">
                <EnvironmentOverview stats={envStats} />
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border h-[450px] overflow-hidden">
              <CardContent className="pt-4 pb-4 px-4 h-full">
                <CriticalAlerts
                  alerts={Array.isArray(alerts) ? [...liveAlerts.filter(a => a.severity === 'critical'), ...alerts].slice(0, 10) : liveAlerts.filter(a => a.severity === 'critical')}
                  onStatusChange={async (id, status) => {
                    await alertsAPI.updateStatus(id, status).catch(() => {});
                    loadDashboardData();
                  }}
                />
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border h-[450px] overflow-hidden">
              <CardContent className="pt-4 pb-4 px-4 h-full">
                <TopVulnerabilities vulnerabilities={vulnerabilities} />
              </CardContent>
            </Card>
          </div>

          {/* Second bottom row */}
          <div className="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-card/50 border-border h-[280px] overflow-hidden">
              <CardContent className="pt-5 h-full">
                <PortStatus stats={portStats} />
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border h-[280px] overflow-hidden">
              <CardContent className="pt-5 h-full">
                <ComplianceStatus compliance={compliance} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color, onClick, testid, badge, pulse }) {
  return (
    <Card
      className={cn(
        "bg-card/50 border-border hover:border-primary/50 transition-all cursor-pointer group relative overflow-hidden",
        pulse && "border-red-500/70 shadow-[0_0_15px_rgba(239,68,68,0.3)]"
      )}
      onClick={onClick}
      data-testid={testid}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
            <p className={cn("text-3xl font-mono font-bold transition-all", color, pulse && "animate-pulse")}>
              {value}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">{subtext}</p>
          </div>
          <Icon className={cn("h-8 w-8 opacity-30 group-hover:opacity-60 transition-opacity", color)} />
        </div>
        {badge && (
          <div className="absolute top-2 right-2">
            <span className="text-[9px] bg-red-500/20 text-red-300 border border-red-500/30 font-mono px-1.5 py-0.5 rounded animate-pulse">
              {badge}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DashboardPage;
